/**
 * The destructive actions for ONE connected account, on its View page.
 *
 * These used to sit as a trash icon in the account card's header row on
 * /sources. That put an irreversible action one mis-click away in a list whose
 * job is scanning, and — because each connector had built its own confirm
 * dance inline — the copy drifted: Slack said "Disconnect", Signal said "Stop
 * new messages", WhatsApp had no confirmation step at all. Moving them here
 * makes /sources a pure index and puts every disconnect behind the same
 * deliberate two-step, with the wording differences that are REAL (what
 * reconnecting costs, what disconnect can and cannot revoke) preserved rather
 * than flattened.
 *
 * What is deliberately NOT unified: the promise each connector can honestly
 * make. Slack revokes its token; Signal stops a linked device; WhatsApp drops a
 * session; Granola CANNOT revoke anything (doctrine 2.5 — there is no revoke
 * API, only Brain's copy of the key to delete). Saying "disconnected" in one
 * voice across all four would make that last one a lie.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { SectionCard } from '@/components/patterns/section-card';
import { Button } from '@/components/ui/button';
import { granolaAccountApi, signalAccountApi, slackAccountApi, wahaSessionApi } from '@/lib/api';
import { qk } from '@/lib/query-client';

interface DisconnectSpec {
  /** Button label. Named for what it actually does to the upstream account. */
  action: string;
  title: string;
  description: string;
  /** Shown only once the user has asked to disconnect — the cost of undoing it. */
  confirmDetail: string;
  run: (accountId: string) => Promise<unknown>;
  /** Invalidated after success so /sources reflects the change on the way back. */
  invalidate: readonly unknown[];
  /** Server-supplied success copy wins when present (Granola's revoke boundary). */
  successMessage?: string;
}

const DISCONNECT: Record<string, DisconnectSpec> = {
  slack: {
    action: 'Disconnect',
    title: 'Disconnect this Slack account',
    description:
      "Revokes Brain's access token and stops new messages. Everything already indexed stays searchable.",
    confirmDetail:
      "This revokes Brain's access to this Slack account and stops new messages. Everything already indexed stays searchable. To connect again you'll need to sign in with Slack once more.",
    run: (id) => slackAccountApi.disconnect(id),
    invalidate: qk.slackAccounts,
    successMessage: 'Disconnected. Messages already indexed stay searchable.',
  },
  signal: {
    action: 'Stop new messages',
    title: 'Stop indexing this Signal account',
    description:
      'Stops new messages from this linked device. Everything already indexed stays searchable.',
    confirmDetail:
      "This stops new messages from this Signal account. Everything already indexed stays searchable. To connect again you'll need to scan a new QR code.",
    run: (id) => signalAccountApi.disconnect(id),
    invalidate: qk.signalAccounts,
    successMessage: 'Stopped. Messages already indexed stay searchable.',
  },
  whatsapp: {
    action: 'Disconnect',
    title: 'Disconnect this WhatsApp account',
    description: 'Ends the session and stops new messages. Indexed history stays searchable.',
    // WhatsApp previously disconnected on a single click with no confirmation
    // at all — the one connector where a mis-click cost a QR re-scan silently.
    confirmDetail:
      "This ends the WhatsApp session and stops new messages. Indexed history stays searchable. To connect again you'll need to scan a new QR code.",
    run: (id) => wahaSessionApi.disconnect(id),
    invalidate: qk.wahaSessions,
    successMessage: 'Disconnected. Indexed history stays searchable.',
  },
  granola: {
    action: 'Disconnect',
    title: 'Disconnect this Granola key',
    description:
      "Deletes Brain's copy of the API key and stops new syncing. Meeting notes already indexed stay searchable.",
    confirmDetail:
      "This deletes Brain's copy of the key and stops new syncing. Everything already indexed stays searchable. Brain cannot revoke the key itself — do that in Granola → Settings → Connectors → API keys.",
    run: (id) => granolaAccountApi.disconnect(id),
    invalidate: qk.granolaAccounts,
  },
};

export function ConnectorDangerZone({
  connector,
  accountId,
}: {
  connector: string;
  accountId: string;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const spec = DISCONNECT[connector];

  const disconnect = useMutation({
    mutationFn: () => (spec as DisconnectSpec).run(accountId),
    onSuccess: (res) => {
      // Granola's server returns the doctrine-2.5 revoke-boundary sentence —
      // display it verbatim rather than keeping a second, drifting copy here.
      const fromServer = (res as { message?: string } | undefined)?.message;
      toast.success(fromServer ?? spec?.successMessage ?? 'Disconnected.');
      void qc.invalidateQueries({ queryKey: spec?.invalidate });
      // The account this page is about is gone from the list now; staying here
      // would show a detail page for something /sources no longer offers.
      void navigate({ to: '/sources' });
    },
    onError: () => toast.error('Failed to disconnect'),
  });

  // An unknown connector gets no destructive UI at all rather than a guess.
  if (!spec) return null;

  return (
    <SectionCard className="mt-6" tone="danger" title={spec.title} description={spec.description}>
      {confirming ? (
        <div className="space-y-3">
          <p className="text-[13px] text-muted-foreground">{spec.confirmDetail}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="destructiveOutline"
              size="sm"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate()}
            >
              {disconnect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : spec.action}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="destructiveOutline" size="sm" onClick={() => setConfirming(true)}>
          <Trash2 className="h-4 w-4" />
          {spec.action}
        </Button>
      )}
    </SectionCard>
  );
}

/**
 * "Delete all Granola data" — Granola-only, and deliberately separate from the
 * disconnect above.
 *
 * Under D5 (Brain never mirrors upstream deletion) this and the per-meeting
 * toggle are the ONLY ways Granola content ever leaves Brain, including for a
 * GDPR erasure request. It purges the single shared per-workspace 'granola'
 * source (§4.2 — one source, N accounts), so running it "from" this account
 * removes EVERY connected Granola account's notes in the workspace. The copy
 * says that outright: this page is per-account, and a reader would otherwise
 * reasonably assume the button's blast radius matches the page's scope.
 */
export function GranolaDeleteAllData({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const deleteAll = useMutation({
    mutationFn: () => granolaAccountApi.deleteData(accountId),
    onSuccess: () => {
      toast.success('Deleting all Granola data — this runs in the background.');
      setConfirming(false);
      void qc.invalidateQueries({ queryKey: qk.granolaAccounts });
      void qc.invalidateQueries({ queryKey: ['granola-meetings'] });
    },
    onError: () => toast.error('Failed to start deletion'),
  });

  return (
    <SectionCard
      className="mt-4"
      tone="danger"
      title="Delete all Granola data"
      description="Permanently removes every meeting note and transcript indexed from ANY connected Granola account in this workspace — not only this one. This cannot be undone, doesn't touch your notes in Granola itself, and doesn't revoke any API keys."
    >
      {confirming ? (
        <div className="flex items-center gap-2">
          <Button
            variant="destructiveOutline"
            size="sm"
            disabled={deleteAll.isPending}
            onClick={() => deleteAll.mutate()}
          >
            {deleteAll.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Yes, delete all Granola data'
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button variant="destructiveOutline" size="sm" onClick={() => setConfirming(true)}>
          <Trash2 className="h-4 w-4" />
          Delete all Granola data
        </Button>
      )}
    </SectionCard>
  );
}
